/**
 * CR426 + CR428: Deal source picker — Sample vs Live toggle with searchable dropdown.
 * CR428: Added computed_value_source and missing_fields to chip display.
 */

import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ChevronDown, Search, Database, Bookmark } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import type { DealSourceMode } from "@/hooks/useDealSourceMode";
import type { LiveDealListItem } from "@/hooks/useDealCardEditorLiveDeals";
import type { EditorSampleDealMeta } from "@/hooks/useDealCardEditorSampleDeal";

interface DealSourcePickerProps {
  mode: DealSourceMode;
  onModeChange: (mode: DealSourceMode) => void;
  meta: EditorSampleDealMeta | null;
  loading?: boolean;
  
  // Live mode props
  liveDeals: LiveDealListItem[];
  liveDealsLoading?: boolean;
  liveSearch: string;
  onLiveSearchChange: (s: string) => void;
  selectedLiveDealId: string | null;
  onSelectLiveDeal: (id: string) => void;
  
  className?: string;
}

export function DealSourcePicker({
  mode,
  onModeChange,
  meta,
  loading,
  liveDeals,
  liveDealsLoading,
  liveSearch,
  onLiveSearchChange,
  selectedLiveDealId,
  onSelectLiveDeal,
  className = "",
}: DealSourcePickerProps) {
  const [open, setOpen] = useState(false);

  // Chip content
  const modeLabel = mode === "live" ? "LIVE" : "SAMPLE";
  const idShort = meta?.deal_id ? meta.deal_id.slice(0, 8) : "—";
  const sourceLabel = meta?.source || "unknown";
  const isError = meta?.source === "none" || !meta?.deal_id;

  // CR428: Render payload diagnostics
  const rp = meta?.renderPayload;
  const dkStatus = rp?.dedupe_key_present ? "present" : "missing";
  const computedSource = rp?.computed_value_source ?? "?";
  const missingFields = rp?.missing_fields ?? [];
  const missingCount = missingFields.length;
  const isMissing = computedSource === "missing";

  const chipParts = [
    modeLabel,
    meta?.environment ?? "?",
    meta?.deal_type ?? "?",
    `id=${idShort}`,
    `dk=${dkStatus}`,
    `src=${sourceLabel}`,
    `computed=${computedSource}`,
  ];
  if (missingCount > 0) {
    chipParts.push(`missing=${missingCount}`);
  }
  const chipText = chipParts.join(" · ");

  const chipClass = isError || isMissing
    ? "text-destructive border-destructive/30"
    : "text-muted-foreground";

  return (
    <div className={`flex items-center gap-2 flex-wrap ${className}`}>
      {/* Mode toggle */}
      <div className="inline-flex rounded-md border border-border overflow-hidden">
        <button
          onClick={() => onModeChange("sample")}
          className={`px-2.5 py-1 text-[10px] font-medium transition-colors ${
            mode === "sample"
              ? "bg-primary text-primary-foreground"
              : "bg-background text-muted-foreground hover:bg-muted"
          }`}
        >
          <Bookmark className="h-3 w-3 inline mr-1" />
          Sample
        </button>
        <button
          onClick={() => onModeChange("live")}
          className={`px-2.5 py-1 text-[10px] font-medium transition-colors ${
            mode === "live"
              ? "bg-primary text-primary-foreground"
              : "bg-background text-muted-foreground hover:bg-muted"
          }`}
        >
          <Database className="h-3 w-3 inline mr-1" />
          Live
        </button>
      </div>

      {/* Chip with optional tooltip for missing fields */}
      {loading ? (
        <Badge variant="outline" className="text-[10px] font-mono opacity-60">
          {modeLabel} · loading…
        </Badge>
      ) : missingCount > 0 ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="outline" className={`text-[10px] font-mono cursor-help ${chipClass}`}>
                {chipText}
              </Badge>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs font-mono max-w-sm">
              <div className="font-medium mb-1">Missing fields:</div>
              {missingFields.map((f) => (
                <div key={f}>• {f}</div>
              ))}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <Badge variant="outline" className={`text-[10px] font-mono ${chipClass}`}>
          {chipText}
        </Badge>
      )}

      {/* Live deal picker dropdown */}
      {mode === "live" && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-6 text-[10px] gap-1">
              {selectedLiveDealId ? `${selectedLiveDealId.slice(0, 8)}…` : "Pick deal"}
              <ChevronDown className="h-3 w-3" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[400px] p-0" align="start">
            <div className="p-2 border-b">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  value={liveSearch}
                  onChange={(e) => onLiveSearchChange(e.target.value)}
                  placeholder="Search deals…"
                  className="h-7 pl-7 text-xs"
                />
              </div>
            </div>
            <ScrollArea className="h-[250px]">
              {liveDealsLoading && (
                <div className="p-4 text-xs text-muted-foreground text-center">Loading…</div>
              )}
              {!liveDealsLoading && liveDeals.length === 0 && (
                <div className="p-4 text-xs text-muted-foreground text-center">
                  No deals found. Try switching to Sample mode.
                </div>
              )}
              {liveDeals.map((d) => (
                <button
                  key={d.deal_id}
                  onClick={() => {
                    onSelectLiveDeal(d.deal_id);
                    setOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 text-xs hover:bg-muted/50 transition-colors border-b last:border-b-0 ${
                    d.deal_id === selectedLiveDealId ? "bg-muted" : ""
                  }`}
                >
                  <div className="font-medium truncate">
                    {d.headline || d.dedupe_key?.slice(0, 40) || d.deal_id.slice(0, 8)}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 text-muted-foreground">
                    <span>{d.merchant_name}</span>
                    <span>·</span>
                    <span>{d.deal_type}</span>
                    <span>·</span>
                    <span>{formatDistanceToNow(new Date(d.updated_at), { addSuffix: true })}</span>
                  </div>
                  <div className="text-[10px] font-mono text-muted-foreground mt-0.5">
                    {d.computed_value_cad != null ? `$${d.computed_value_cad}` : ""}
                    {d.effective_percent_back != null ? ` ${d.effective_percent_back}%` : ""}
                  </div>
                </button>
              ))}
            </ScrollArea>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
